import { useState, useMemo, useEffect } from 'react';
import { apiFetch, API_BASE } from '../services/api';


export const useMails = (user) => {
    const [activeFolder, setActiveFolder] = useState('INBOX');
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isComposeOpen, setIsComposeOpen] = useState(false);
    const [selectedEmailId, setSelectedEmailId] = useState(null);
    const [emails, setEmails] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [storageUsage, setStorageUsage] = useState(null);
    const [composeData, setComposeData] = useState(null);
    const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
    const [availableIdentities, setAvailableIdentities] = useState([]);
    const [loadedFolder, setLoadedFolder] = useState(null);

    const MAILS_API_BASE = `${API_BASE}/mails`;

    const fetchStorageUsage = async () => {
        if (!user) return;
        try {
            const response = await apiFetch('/mails/usage');
            if (response.ok) {
                const data = await response.json();
                setStorageUsage(data);
            }
        } catch (error) {
            console.error('Failed to fetch storage usage:', error);
        }
    };

    const fetchIdentities = async () => {
        if (!user) return;
        try {
            const response = await apiFetch('/mails/m-boxes');
            if (response.ok) {
                const folders = await response.json();
                // Identities are the user's primary email + any folder starting with "Shared/"
                const shared = folders
                    .filter(f => f.startsWith('Shared/'))
                    .map(f => f.substring(7));

                const uniqueIdentities = Array.from(new Set([user.email, ...shared]));
                setAvailableIdentities(uniqueIdentities);
            }
        } catch (error) {
            console.error('Failed to fetch identities:', error);
        }
    };

    const refreshInboxUnreadCount = async () => {
        if (!user) return;
        try {
            const response = await apiFetch('/mails/unread-count/INBOX');
            if (response.ok) {
                const data = await response.json();
                setInboxUnreadCount(data.count);
            }
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
        }
    };

    const loadEmailsFromDb = async () => {
        if (!user) return;
        const requestedFolder = activeFolder;
        // 🚀 THE FIX: Use local cache to bypass "Loading Screen" immediately
        const cacheKey = `xemail_cache_${user.email}_${requestedFolder}`;
        const cached = localStorage.getItem(cacheKey);
        
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                setEmails(parsed);
                setLoadedFolder(requestedFolder);
                setIsLoading(false); // Skip loading screen!
            } catch (e) {
                console.error("Cache error", e);
            }
        } else {
            setIsLoading(true);
            if (loadedFolder !== requestedFolder) {
                setEmails([]);
            }
        }

        try {
            const response = await apiFetch(`/mails/db/${requestedFolder}`);

            if (activeFolder !== requestedFolder) return;

            if (response.ok) {
                const data = await response.json();
                if (activeFolder !== requestedFolder) return;

                const mappedMails = data.map(m => ({
                    ...m,
                    sender: requestedFolder === 'Sent' ? m.to : m.from,
                    time: m.timestamp,
                    folder: requestedFolder,
                }));

                setEmails(mappedMails);
                setLoadedFolder(requestedFolder);
                setError(null);

                if (requestedFolder === 'INBOX' || requestedFolder === 'Inbox') {
                    setInboxUnreadCount(data.filter(m => !m.isRead).length);
                }

                // ALWAYS trigger background sync, but it will be handled by syncMails
                syncMails(requestedFolder);
            } else {
                setError(`Failed to load from DB (Status: ${response.status})`);
                // If DB fails, try a direct sync anyway
                syncMails(requestedFolder);
            }
        } catch (error) {
            console.error('Failed to load DB mails:', error);
            setError('Connection error');
        } finally {
            if (activeFolder === requestedFolder) {
                setIsLoading(false);
            }
        }
    };

    const syncMails = async (folderToSync = activeFolder) => {
        if (!user) return;
        setIsSyncing(true);
        try {
            const response = await apiFetch('/mails/fetch', {
                method: 'POST',
                body: JSON.stringify({
                    folder: folderToSync
                })
            });

            // Check if user has navigated away while we were syncing
            if (activeFolder !== folderToSync) {
                console.log(`Ignoring stale sync for ${folderToSync}, current is ${activeFolder}`);
                return;
            }

            if (response.ok) {
                const data = await response.json();

                // Double check after parsing JSON
                if (activeFolder !== folderToSync) return;

                const mapped = data.map(m => ({
                    ...m,
                    sender: folderToSync === 'Sent' ? m.to : m.from,
                    time: m.timestamp,
                    folder: folderToSync,
                }));
                setEmails(mapped);
                // Persistence for Zero-Loading experience
                localStorage.setItem(`xemail_cache_${user.email}_${folderToSync}`, JSON.stringify(mapped));
                setLoadedFolder(folderToSync);
                setError(null);
                if (folderToSync === 'INBOX' || folderToSync === 'Inbox') {
                    setInboxUnreadCount(data.filter(m => !m.isRead).length);
                }
            } else {
                const errText = await response.text();
                setError(errText || 'Failed to sync mails');
            }
        } catch (error) {
            console.error('Failed to sync mails:', error);
            setError('Connection error');
        } finally {
            if (activeFolder === folderToSync) {
                setIsSyncing(false);
            }
        }
    };

    useEffect(() => {
        if (user) {
            fetchStorageUsage();
        }
    }, [user, emails]);

    useEffect(() => {
        if (user) {
            loadEmailsFromDb();
            refreshInboxUnreadCount();
        }
    }, [user, activeFolder]);

    useEffect(() => {
        if (!user) return;
        fetchIdentities();
        const interval = setInterval(refreshInboxUnreadCount, 30000);
        return () => clearInterval(interval);
    }, [user]);

    const filteredEmails = useMemo(() => {
        if (!searchQuery) return emails;
        const q = searchQuery.toLowerCase();
        return emails.filter(email =>
            email.sender.toLowerCase().includes(q) ||
            email.subject.toLowerCase().includes(q)
        );
    }, [emails, searchQuery]);

    const unreadCount = emails.filter(e => !e.isRead).length;

    const toggleSelect = (id) => {
        setSelectedEmails(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const markAsRead = async (id) => {
        setEmails(prev => {
            const next = prev.map(e => e.id === id ? { ...e, isRead: true } : e);
            if (activeFolder === 'INBOX' || activeFolder === 'Inbox') {
                setInboxUnreadCount(next.filter(e => !e.isRead).length);
            }
            return next;
        });
        try {
            await apiFetch('/mails/mark-read', {
                method: 'POST',
                body: JSON.stringify({ id, isRead: true })
            });
        } catch (err) {
            console.error('Failed to mark as read:', err);
        } finally {
            refreshInboxUnreadCount();
        }
    };

    const toggleRead = async (id) => {
        let nextReadStatus = false;
        setEmails(prev => {
            const next = prev.map(e => {
                if (e.id === id) {
                    nextReadStatus = !e.isRead;
                    return { ...e, isRead: nextReadStatus };
                }
                return e;
            });
            if (activeFolder === 'INBOX' || activeFolder === 'Inbox') {
                setInboxUnreadCount(next.filter(e => !e.isRead).length);
            }
            return next;
        });

        try {
            await apiFetch('/mails/mark-read', {
                method: 'POST',
                body: JSON.stringify({ id, isRead: nextReadStatus })
            });
        } catch (err) {
            console.error('Failed to toggle read status:', err);
        } finally {
            refreshInboxUnreadCount();
        }
    };

    const toggleStar = async (id, isStarred) => {
        // Toggle locally first for responsiveness
        setEmails(prev => prev.map(e => e.id === id ? { ...e, isImportant: isStarred } : e));

        try {
            await apiFetch('/mails/mark-important', {
                method: 'POST',
                body: JSON.stringify({ id, isImportant: isStarred })
            });

            // If we are currently in the 'Important' folder and unstarring, remove from view
            if (activeFolder === 'Important' && !isStarred) {
                setEmails(prev => prev.filter(e => e.id !== id));
            }
        } catch (err) {
            console.error('Failed to toggle star status:', err);
            // Rollback on error
            setEmails(prev => prev.map(e => e.id === id ? { ...e, isImportant: !isStarred } : e));
        }
    };

    const selectAll = () => {
        if (selectedEmails.length === filteredEmails.length && filteredEmails.length > 0) {
            setSelectedEmails([]);
        } else {
            setSelectedEmails(filteredEmails.map(e => e.id));
        }
    };

    const deletePermanently = async (ids, silent = false) => {
        if (!user || ids.length === 0) return;
        try {
            const response = await apiFetch('/mails/delete-permanent', {
                method: 'POST',
                body: JSON.stringify({
                    ids
                })

            });


            if (response.ok) {
                setEmails(prev => prev.filter(e => !ids.includes(e.id)));
                setSelectedEmails([]);
                if (!silent) {
                    alert('Permanently deleted');
                }
            } else {
                const errText = await response.text();
                console.error('Permanent delete failed:', response.status, errText);
                alert(`Failed to delete permanently (Status ${response.status}): ${errText || 'No response from server'}`);
            }
        } catch (err) {
            console.error('Permanent delete error:', err);
            alert('Failed to delete permanently');
        }
    };

    const handleRestore = async (ids) => {
        if (!user || ids.length === 0) return;
        try {
            const response = await apiFetch('/mails/restore-from-trash', {
                method: 'POST',
                body: JSON.stringify({
                    ids
                })
            });

            if (response.ok) {
                setEmails(prev => prev.filter(e => !ids.includes(e.id)));
                setSelectedEmails([]);
                alert('✅ Emails restored successfully!');
            } else {
                const errText = await response.text();
                console.error('Restore failed:', response.status, errText);
                alert(`Failed to restore (Status ${response.status}): ${errText || 'No response from server'}`);
            }
        } catch (err) {
            console.error('Restore error:', err);
            alert('Failed to restore emails');
        }
    };

    const handleUndo = async (ids) => {
        if (!user || ids.length === 0) return;
        try {
            const response = await apiFetch('/mails/undo-from-archive', {
                method: 'POST',
                body: JSON.stringify({
                    ids
                })
            });

            if (response.ok) {
                setEmails(prev => prev.filter(e => !ids.includes(e.id)));
                setSelectedEmails([]);
                alert('✅ Emails moved back to their original folder!');
            } else {
                const errText = await response.text();
                console.error('Undo failed:', response.status, errText);
                alert(`Failed to undo (Status ${response.status}): ${errText || 'No response from server'}`);
            }
        } catch (err) {
            console.error('Undo error:', err);
            alert('Failed to undo archive action');
        }
    };

    const handleAction = async (targetFolder, specificId = null) => {
        const idsToMove = specificId ? [specificId] : selectedEmails;
        if (idsToMove.length === 0) return;

        // Handle Restore action from Trash
        if (targetFolder === 'Restore' && activeFolder === 'Trash') {
            handleRestore(idsToMove);
            return;
        }

        // Handle Undo action from Archive
        if (targetFolder === 'Undo' && activeFolder === 'Archive') {
            handleUndo(idsToMove);
            return;
        }

        // If explicitly moving to Trash but already in Trash, or if user clicked Delete in Trash
        if (activeFolder === 'Trash' && targetFolder === 'Trash') {
            const confirmed = window.confirm('Permanently delete selected emails? This cannot be undone.');
            if (confirmed) {
                deletePermanently(idsToMove);
            }
            return;
        }

        const previousEmails = [...emails];
        setEmails(prev => {
            const next = prev.filter(email => !idsToMove.includes(email.id));
            if (activeFolder === 'INBOX' || activeFolder === 'Inbox') {
                setInboxUnreadCount(next.filter(e => !e.isRead).length);
            }
            return next;
        });

        try {
            const response = await apiFetch('/mails/move-folder', {
                method: 'POST',
                body: JSON.stringify({
                    ids: idsToMove,
                    targetFolder: targetFolder
                })

            });


            if (!response.ok) {
                setEmails(previousEmails);
                alert(`Failed to move mails`);
            } else {
                // 🔥 Success: Clear selection after action
                setSelectedEmails([]);
            }
        } catch (error) {
            setEmails(previousEmails);
        } finally {
            refreshInboxUnreadCount();
        }
    };

    const handleRefresh = () => {
        syncMails();
        setSelectedEmails([]);
    };

    const addSentEmail = async (composeData) => {
        if (!user) return;

        console.log('Sending email:', {
            from: user.email,
            to: composeData.to,
            hasPassword: !!user.password,
            passwordLength: user.password?.length || 0
        });

        try {
            const formData = new FormData();
            // from is now handled by JWT

            formData.append('password', user.password);
            formData.append('to', composeData.to.trim());
            formData.append('cc', composeData.cc || '');
            formData.append('bcc', composeData.bcc || '');
            formData.append('subject', composeData.subject);
            formData.append('body', composeData.body);

            if (composeData.attachments) {
                composeData.attachments.forEach(file => {
                    formData.append('attachments', file);
                });
            }

            // Ensure from is sent if it's different from user's primary email
            if (composeData.from && composeData.from !== user.email) {
                formData.append('from', composeData.from);
            }

            const response = await apiFetch('/mails/send', {
                method: 'POST',
                body: formData
            });



            if (!response.ok) {
                // Try to get detailed error message from backend
                const errorData = await response.json().catch(() => null);
                const errorMsg = errorData?.error || `HTTP ${response.status}: ${response.statusText}`;
                console.error('❌ Send failed:', errorMsg);
                throw new Error(errorMsg);
            }

            // If this was a draft being sent, delete the draft
            if (composeData.draftId) {
                try {
                    await deletePermanently([composeData.draftId], true);
                } catch (err) {
                    console.error('Failed to delete draft after sending:', err);
                }
            }

            alert('✅ Mail sent successfully!');
            setActiveFolder('Sent');
        } catch (err) {
            console.error('❌ Send error:', err);
            alert(`❌ Send failed: ${err.message || 'Unknown error'}`);
        }
    };

    const saveDraftEmail = async (composeData) => {
        if (!user) return;
        try {
            const formData = new FormData();
            // from is now handled by JWT

            formData.append('to', composeData.to || '');
            formData.append('cc', composeData.cc || '');
            formData.append('bcc', composeData.bcc || '');
            formData.append('subject', composeData.subject || '');
            formData.append('body', composeData.body || '');

            // If editing an existing draft, send the draftId so backend can delete the old one
            if (composeData.draftId) {
                formData.append('draftId', composeData.draftId);
            }

            if (composeData.attachments) {
                composeData.attachments.forEach(file => {
                    formData.append('attachments', file);
                });
            }

            const response = await apiFetch('/mails/save-draft', {
                method: 'POST',
                body: formData
            });



            if (!response.ok) {
                const errorText = await response.text();
                console.error('Draft save failed:', errorText);
                throw new Error(errorText || 'Failed to save draft');
            }

            alert('Draft saved!');

            // Refresh the drafts folder to show the updated draft
            if (activeFolder === 'Drafts') {
                loadEmailsFromDb();
            }
        } catch (err) {
            console.error('Draft error:', err);
            alert(`Draft failed: ${err.message}`);
        }
    };

    const handleReply = (email) => {
        setComposeData({
            to: email.from,
            subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
            body: `<br><br>On ${new Date(email.timestamp || Date.now()).toLocaleString()}, ${email.from} wrote:<br><blockquote>${email.body || ''}</blockquote>`
        });
        setIsComposeOpen(true);
    };

    const handleForward = (email) => {
        setComposeData({
            to: '',
            subject: email.subject.startsWith('Fwd:') ? email.subject : `Fwd: ${email.subject}`,
            body: `<br><br>--- Forwarded message ---<br>From: ${email.from}<br>Date: ${new Date(email.timestamp || Date.now()).toLocaleString()}<br>Subject: ${email.subject}<br>To: ${email.to}<br><br>${email.body || ''}`
        });
        setIsComposeOpen(true);
    };

    const handleReplyAll = (email) => {
        const extractEmail = (str) => {
            if (!str) return "";
            if (str.includes('<')) {
                return str.substring(str.indexOf('<') + 1, str.indexOf('>')).trim().toLowerCase();
            }
            return str.trim().toLowerCase();
        };

        const userEmail = extractEmail(user.email);
        const senderEmail = extractEmail(email.from);

        const allTo = email.to ? email.to.split(',').map(s => s.trim()) : [];
        const allCc = email.cc ? email.cc.split(',').map(s => s.trim()) : [];

        const otherRecipients = [...allTo, ...allCc].filter(addr => {
            const cleanAddr = extractEmail(addr);
            return cleanAddr !== userEmail && cleanAddr !== senderEmail && cleanAddr !== "";
        });

        setComposeData({
            to: email.from,
            cc: otherRecipients.join(', '),
            subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
            body: `<br><br>On ${new Date(email.timestamp || Date.now()).toLocaleString()}, ${email.from} wrote:<br><blockquote>${email.body || ''}</blockquote>`
        });
        setIsComposeOpen(true);
    };


    const handleEditDraft = (email) => {
        setComposeData({
            to: email.to || '',
            cc: email.cc || '',
            bcc: email.bcc || '',
            subject: email.subject || '',
            body: email.body || '',
            draftId: email.id // Store draft ID so we can delete it when sent
        });
        setIsComposeOpen(true);
    };

    const resetCompose = () => {
        setComposeData(null);
        setIsComposeOpen(false);
    };

    useEffect(() => {
        setSelectedEmailId(null);
        setSelectedEmails([]);
    }, [activeFolder]);

    return {
        activeFolder,
        setActiveFolder,
        emails,
        filteredEmails,
        unreadCount,
        inboxUnreadCount,
        selectedEmails,
        setSelectedEmails,
        searchQuery,
        setSearchQuery,
        selectedEmailId,
        setSelectedEmailId,
        isComposeOpen,
        setIsComposeOpen,
        refreshInboxUnreadCount,
        handleRefresh,
        handleAction,
        selectAll,
        toggleSelect,
        toggleRead,
        markAsRead,
        toggleStar,
        addSentEmail,
        saveDraftEmail,
        handleReply,
        handleReplyAll,
        handleForward,
        handleEditDraft,
        resetCompose,
        composeData,
        availableIdentities,
        isLoading,
        isSyncing,
        loadedFolder,
        error,
        storageUsage
    };
};
